import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import {
  User,
  Lock,
  Phone,
  Briefcase,
  Building2,
  Shield,
  Key,
  FileText,
  Plus,
  Mail,
} from "lucide-react";
import policyOptions from "../jsons/policyOptions.json";
import useAuth from "../useAuth";

export default function RegisterUser() {
  const [formData, setFormData] = useState({
    password: "",
    fullName: "",
    mobileNo: "",
    email: "",
    designation: "",
    userType: "",
    vlanId: "20", // Default as per existing logic semblance
    receivedDepartment: "Admin", // Default
    policy: "",
    key: "",
    isReadOnly: false,
    isReadOnly: false,
    isVerified: true,
    departmentId: null,
  });


  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departmentsList, setDepartmentsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const userTypes = ["Minister", "Secretary", "Director", "Others"];

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem("dbToken");
        const response = await axios.get(`${server}/department/getAll`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            pageNo: 1,
            pageSize: 1000,
          },
        });
        if (response.data.success) {
          setDepartmentsList(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        toast.error("Failed to load departments.");
      }
    };

    fetchDepartments();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUserTypeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      userType: value,
      vlanId: value !== "Others" ? "20" : prev.vlanId,
      receivedDepartment:
        value !== "Others" ? "Admin" : prev.receivedDepartment,
    }));
    if (value !== "Others") {
      setSelectedDepartment("");
    }
  };

  const handleDepartmentChange = (e) => {
    const deptName = e.target.value;
    setSelectedDepartment(deptName);

    const deptObj = departmentsList.find((d) => d.departmentName === deptName);
    if (deptObj) {
      setFormData((prev) => ({
        ...prev,
        receivedDepartment: deptObj.departmentName,
        vlanId: deptObj.vlanId,
        departmentId: deptObj.id,
      }));
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const nameParts = formData.fullName.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ") || ".";

    const payload = {
      username: String(formData.mobileNo),
      password: formData.password,
      fullName: formData.fullName,
      phoneNumber: String(formData.mobileNo),
      email: formData.email,
      department: formData.receivedDepartment,
      policiesMapperId: formData.policy,
      isVerified: formData.isVerified,
      departmentId: formData.departmentId,
    };

    const authPayload = {
      mobileNo: Number(formData.mobileNo),
      password: formData.password,
      fullName: formData.fullName,
      designation: formData.designation,
      userType: formData.userType,
      vlanId: Number(formData.vlanId),
      department: formData.receivedDepartment,
      policy: formData.policy,
      status: true,
      key: formData.key,
      isReadOnly: formData.isReadOnly,
    };

    try {
      const token = localStorage.getItem("dbToken");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${server}/wifiusers/new`, payload, {
        headers,
      });

      await axios.post(`${server}/auth/register`, authPayload, {
        headers,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("User Registration Successful!");
        setFormData({
          mobileNo: "",
          email: "",
          password: "",
          fullName: "",
          designation: "",
          userType: "",
          vlanId: "20",
          receivedDepartment: "Admin",
          policy: "",
          key: "",
          isReadOnly: false,
          isVerified: true,
          departmentId: null,
        });
        setSelectedDepartment("");
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration Error:", error);
      toast.error(
        error.response?.data?.message ||
        "An error occurred during registration.",
      );
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            User Registration
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Create new user accounts and assign roles.
          </p>
        </div>
      </div>

      {/* Main Form Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100">
          <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Account Details</h2>
            <p className="text-xs text-gray-500">
              Enter the user's personal and role information
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Full Name
              </label>
              <div className="relative group">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  required
                />
              </div>
            </div>

            {/* Mobile No */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Mobile No / Username
              </label>
              <div className="relative group">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <input
                  type="number"
                  name="mobileNo"
                  value={formData.mobileNo}
                  onChange={handleChange}
                  placeholder="Enter 10-digit mobile no"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  required
                  maxLength={10}
                  onInput={(e) => {
                    if (e.target.value.length > 10)
                      e.target.value = e.target.value.slice(0, 10);
                  }}
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Email
              </label>
              <div className="relative group">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Password
              </label>
              <div className="relative group">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                  required
                />
              </div>
            </div>

            {/* Designation */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Designation
              </label>
              <div className="relative group">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  placeholder="Enter designation"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                />
              </div>
            </div>

            {/* User Type */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                User Type
              </label>
              <div className="relative group">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <select
                  name="userType"
                  value={formData.userType}
                  onChange={handleUserTypeChange}
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                  required
                >
                  <option value="" disabled>
                    Select User Type
                  </option>
                  {userTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Conditional Department Selection */}
            {formData.userType === "Others" && (
              <div className="space-y-2 md:col-span-2 bg-blue-50 p-4 rounded-xl border border-blue-100 animate-in fade-in slide-in-from-top-2">
                <label className="text-xs font-bold text-blue-700 uppercase tracking-wider block">
                  Select Department
                </label>
                <div className="relative group mt-2">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-400" />
                  <select
                    value={selectedDepartment}
                    onChange={handleDepartmentChange}
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                    required={formData.userType === "Others"}
                  >
                    <option value="" disabled>
                      Select Department
                    </option>
                    {departmentsList.map((dept, index) => (
                      <option key={index} value={dept.departmentName}>
                        {dept.departmentName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* VLAN ID */}
            {formData.userType === "Others" && (
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                  VLAN ID
                </label>
                <div className="relative group">
                  {/* Generic Building/Network Icon */}
                  <input
                    type="number"
                    name="vlanId"
                    value={formData.vlanId}
                    readOnly
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono font-medium text-gray-500 cursor-not-allowed focus:outline-none"
                  />
                </div>
              </div>
            )}

            {/* Received Department (Read-only or Input) */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Assigned Department
              </label>
              <div className="relative group">
                {formData.userType === "Others" ? (
                  <>
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      name="receivedDepartment"
                      value={formData.receivedDepartment}
                      onChange={handleChange}
                      placeholder="Enter department"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                    />
                  </>
                ) : (
                  <div className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-500 cursor-not-allowed">
                    Admin
                  </div>
                )}
              </div>
            </div>

            {/* Policy */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Policy
              </label>
              <div className="relative group">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <select
                  name="policy"
                  value={formData.policy}
                  onChange={handleChange}
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                  required
                >
                  <option value="" disabled>
                    Select Policy
                  </option>
                  {policyOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Key */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Key
              </label>
              <div className="relative group">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <input
                  type="text"
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  placeholder="Enter key"
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                />
              </div>
            </div>

            {/* IsReadOnly Checkbox */}
            <div className="md:col-span-1 space-y-2">
              <div
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    isReadOnly: !prev.isReadOnly,
                  }))
                }
              >
                <div
                  className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.isReadOnly ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
                >
                  {formData.isReadOnly && (
                    <span className="text-white text-xs font-bold">✓</span>
                  )}
                </div>
                <input
                  type="checkbox"
                  name="isReadOnly"
                  checked={formData.isReadOnly}
                  onChange={handleChange}
                  className="hidden"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 select-none">
                    Is Read Only User
                  </span>
                  <p className="text-xs text-gray-500 select-none">
                    View-only access
                  </p>
                </div>
              </div>
            </div>

            {/* IsVerified Checkbox */}
            <div className="md:col-span-1 space-y-2">
              <div
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    isVerified: !prev.isVerified,
                  }))
                }
              >
                <div
                  className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.isVerified ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
                >
                  {formData.isVerified && (
                    <span className="text-white text-xs font-bold">✓</span>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900 select-none">
                    Is Verified
                  </span>
                  <p className="text-xs text-gray-500 select-none">
                    Mark user as verified
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-gray-100">
            <button
              type="submit"
              disabled={loading}
              className={`flex items-center justify-center gap-2 w-full mx-auto md:w-auto md:min-w-[200px] px-6 py-2.5 rounded-lg font-medium text-white shadow-sm transition-all ${loading
                ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 hover:shadow"
                }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                  <span>Registering User...</span>
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  <span>Register User</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
